import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { useMemo } from "react";
import { useInView } from "../../hooks/useInView";
import { useCmsLink } from "../../hooks/useCmsLink";
import { useHomePage } from "../../context/HomePageContext";
import { useLanguage } from "../../context/LanguageContext";
import { resolveCategories } from "../../lib/homePageContent";

export default function ProductCategorySection() {
  const goTo = useCmsLink();
  const { ref, inView } = useInView<HTMLDivElement>();
  const { data } = useHomePage();
  const { language } = useLanguage();
  const categories = useMemo(
    () => resolveCategories(data?.categories, language),
    [data, language],
  );

  return (
    <section className="relative w-full overflow-hidden py-32">
      <div ref={ref} className="mx-auto w-full max-w-[1440px] px-6 lg:px-20">
        <Swiper
          modules={[Pagination, Navigation]}
          spaceBetween={32}
          grabCursor
          pagination={{ clickable: true }}
          navigation={false}
          breakpoints={{
            0: {
              slidesPerView: 1.2,
            },
            640: {
              slidesPerView: 1.5,
            },
            768: {
              slidesPerView: 2.2,
            },
            1024: {
              slidesPerView: 3.2,
            },
          }}
          className="product-swiper pb-14 pl-6 lg:pl-20">
          {categories.map((item, index) => (
            <SwiperSlide key={item.key}>
              <div
                onClick={() => goTo(item.link)}
                style={{
                  transitionDelay: inView ? `${index * 90}ms` : "0ms",
                }}
                className={`
      group
      reveal-scale
      ${inView ? "is-visible" : ""}

      relative
      w-full
      max-w-[343px]
      min-h-[420px]
      h-full

      overflow-hidden
      rounded-[32px]

      bg-gradient-to-br
      ${item.gradient}

      ${item.link ? "cursor-pointer" : ""}
      p-[18px]
      sm:p-[22px]

      transition-all
      duration-300

      hover:-translate-y-2
      hover:shadow-xl
    `}>
                {/* IMAGE CONTAINER */}
                <div
                  className="
        relative
        w-full
        aspect-square

        overflow-hidden
        rounded-[30px]

        bg-white

        flex
        items-center
        justify-center
      ">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="
          h-full
          w-full

          object-contain

          transition-transform
          duration-500

          group-hover:scale-105
        "
                  />
                </div>

                {/* TITLE + ARROW */}
                <div
                  className="
        mt-6
        sm:mt-8

        flex
        items-center
        justify-between
      ">
                  <h3
                    className="
          text-[26px]
          sm:text-[42px]
          font-bold
          leading-tight
          sm:leading-none
          text-white
        ">
                    {item.title}
                  </h3>

                  <span
                    className="
          shrink-0
          text-[26px]
          sm:text-[42px]
          font-light
          leading-none
          text-white

          transition-transform
          duration-300

          group-hover:translate-x-2
        ">
                    →
                  </span>
                </div>

                {/* DESCRIPTION */}
                {item.description && (
                  <p
                    className="
          mt-3
          sm:mt-6

          max-w-[300px]

          text-[14px]
          sm:text-[20px]
          leading-[1.3]
          sm:leading-[1.25]

          text-white

          line-clamp-3
        ">
                    {item.description}
                  </p>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
}
